<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn dark color="primary" v-if="hasPermissions(['order_store'])" @click="dialogs.addDialog = true">
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.purchase_id }}</td>-->
          <td>{{ props.item.bill_auto_number }}</td>
          <td>{{ props.item.outlet_sent }}</td>
          <td>{{ props.item.outlet_received }}</td>
          <td>{{ props.item.total_amount | currency }}</td>
          <td>{{ $t(props.item.asset_type) }}</td>
          <td>{{ props.item.bill_date }}</td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="props.item.transfer_sent_status === 'Sent' ? 'success' : 'warning'"
                text-color="white"
                class="no-select"
              >
                <v-icon class="rotate-icon-330">
                  {{ props.item.transfer_sent_status === 'Sent' ? 'send' : 'fa-spinner' }}
                </v-icon>
              </v-chip>
              <span>
                {{ props.item.transfer_sent_status === 'Sent' ? $t('Sent') : $t('Pending') }}
              </span>
            </v-tooltip>
          </td>
          <td>
            <v-tooltip top>
              <v-chip
                slot="activator"
                label
                :color="props.item.transfer_received_status === 'Received' ? 'success' : 'warning'"
                text-color="white"
                class="no-select"
              >
                <v-icon class="rotate-icon-150">
                  {{ props.item.transfer_received_status === 'Received' ? 'send' : 'fa-spinner' }}
                </v-icon>
              </v-chip>
              <span>
                {{ props.item.transfer_received_status === 'Received' ? $t('Received') : $t('Pending') }}
              </span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['order_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.purchase_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">
                      {{ $t('datatable-view') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Order information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="
                    hasPermissions(['order_update']) &&
                      props.item.transfer_received_status === 'Pending' &&
                      props.item.transfer_sent_status === 'Pending'
                  "
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="editRecord(props.item.purchase_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">
                      {{ $t('datatable-edit') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Order information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="
                    hasPermissions(['received_approve']) &&
                      props.item.transfer_received_status === 'Pending' &&
                      props.item.transfer_sent_status === 'Sent'
                  "
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="approveRecord(props.item.purchase_id)">
                    <v-icon>fa-thumbs-up</v-icon>
                    <v-list-tile-content class="px-3">
                      {{ $t('datatable-approve') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Approve Order`) }}</span>
                </v-tooltip>

                <v-divider
                  v-if="
                    hasPermissions(['order_destroy']) &&
                      props.item.transfer_received_status === 'Pending' &&
                      props.item.transfer_sent_status === 'Pending'
                  "
                />

                <v-tooltip
                  v-if="
                    hasPermissions(['order_destroy']) &&
                      props.item.transfer_received_status === 'Pending' &&
                      props.item.transfer_sent_status === 'Pending'
                  "
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.purchase_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">
                      {{ $t('datatable-delete') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Order Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">
          {{ $t('Sorry, No results found for your search criteria') }}
        </v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-receive
      :addDialog="dialogs.addDialog"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
    />

    <edit-receive
      :editDialog="dialogs.editDialog"
      @updated="updateDatatableResult(), (dialogs.editDialog = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
    />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <view-receive :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    addReceive: () => import('./addReceive.vue'),
    editReceive: () => import('./editReceive.vue'),
    viewReceive: () => import('./viewReceive.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'purchase_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'purchase_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Bill Number'), value: 'bill_auto_number' },
        { text: this.$t('Sent Outlet'), value: 'outlet_sent' },
        { text: this.$t('Received Outlet'), value: 'outlet_received' },
        {
          text: `${this.$t('Amount')} (${localStorage.systemCurrency})`,
          value: 'total_amount'
        },
        { text: this.$t('Asset Type'), value: 'asset_type' },
        { text: this.$t('Bill Date'), value: 'bill_date' },
        { text: this.$t('Sent Status'), value: 'transfer_sent_status' },
        { text: this.$t('Received Status'), value: 'transfer_received_status' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Orders & Receives'),
      icon: 'fa-dolly'
    })

    this.setPurchaseProductsList()
    this.setTransferOutletsList()
    this.updateFullFixedAssetsList()
    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }
  },

  computed: {
    ...mapGetters({
      resultData: 'receives/getReceiveList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'receives/updateReceiveList',
      deleteRecord: 'receives/deleteReceive',
      fetchReceiveInfo: 'receives/fetchReceiveInfo',
      setPurchaseProductsList: 'products/setFullPurchaseProductsList',
      setTransferOutletsList: 'transfers/setTransferOutletsList',
      approveReceive: 'receives/approveReceive',
      updateFullFixedAssetsList: 'fixedAssets/updateFullTransferFixedAssetsList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['order_show', 'order_update', 'order_destroy', 'received_approve'])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('order_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    approveRecord(id) {
      this.approveReceive(id).then(() => {
        let payload = { ...this.pagination, search: this.search }
        this.updateResult(payload)
      })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchReceiveInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.fetchReceiveInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = { ...this.pagination, search: this.search }
          this.updateResult(payload)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    }
  }
}
</script>

<style scoped />
